import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, Clock, MapPin, Phone, UtensilsCrossed } from 'lucide-react';

/**
 * Virtual Host Order Success Page
 *
 * Shown to customers after completing payment for a phone order.
 * Simple, clean confirmation with order details.
 * Shows restaurant name (not Dine AI logo) since customer ordered from restaurant.
 *
 * Route: /order/:orderId/success
 */
const VirtualHostOrderSuccess = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const token = searchParams.get('token');

  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string>('');

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setError('Order not found');
        setLoading(false);
        return;
      }

      try {
        // Fetch order details from API, passing session_id for payment verification and token for auth
        const params = new URLSearchParams();
        if (sessionId) params.set('session_id', sessionId);
        if (token) params.set('token', token);
        const qs = params.toString();
        const url = `/api/virtual-host/payment/${orderId}/confirmation${qs ? `?${qs}` : ''}`;
        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          setOrderDetails(data);
          if (data.restaurant_name) {
            setRestaurantName(data.restaurant_name);
          }
        } else {
          // Even if fetch fails, show generic success
          // Payment already went through Stripe
          console.log('Could not fetch order details, showing generic success');
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        // Don't show error - payment succeeded, just can't get details
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex flex-col">
      {/* Header - Blue with restaurant name */}
      <header className="bg-[#1e3a5f] p-4 flex items-center justify-center gap-3">
        <UtensilsCrossed className="w-6 h-6 text-white" />
        <h1 className="text-white text-lg font-semibold">
          {restaurantName || 'Your Order'}
        </h1>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          {/* Success Icon */}
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </div>

          {/* Success Message */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          <p className="text-gray-600 mb-6">
            Thank you for your order. Your food is being prepared.
          </p>

          {/* Order Info Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 text-left">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : orderDetails ? (
              <>
                {/* Order Number */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">#</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Order Number</p>
                    <p className="font-medium text-gray-900">{orderId}</p>
                  </div>
                </div>

                {/* Pickup Time */}
                {orderDetails.pickup_time && (
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <Clock className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pickup Time</p>
                      <p className="font-medium text-gray-900">{orderDetails.pickup_time}</p>
                    </div>
                  </div>
                )}

                {/* Restaurant Address */}
                {orderDetails.restaurant_address && (
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pickup Location</p>
                      <p className="font-medium text-gray-900">{orderDetails.restaurant_address}</p>
                    </div>
                  </div>
                )}

                {/* Restaurant Phone */}
                {orderDetails.restaurant_phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Phone className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Questions?</p>
                      <a
                        href={`tel:${orderDetails.restaurant_phone}`}
                        className="font-medium text-green-600 hover:text-green-700"
                      >
                        {orderDetails.restaurant_phone}
                      </a>
                    </div>
                  </div>
                )}

                {/* Order Total */}
                {orderDetails.total && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Paid</span>
                      <span className="text-xl font-bold text-gray-900">
                        ${orderDetails.total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Generic success when details unavailable */
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">#</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Order Number</p>
                    <p className="font-medium text-gray-900">{orderId}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Your order has been confirmed. The restaurant will have it ready for pickup.
                </p>
              </>
            )}
          </div>

          {/* Footer Note */}
          <p className="text-sm text-gray-500">
            A confirmation has been sent to your phone.
          </p>

          {/* Powered by Dine AI + Terms Link */}
          <div className="mt-8 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-400">
              Powered by{' '}
              <a
                href="https://dineai.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-600 underline"
              >
                Dine AI
              </a>
            </p>
            <p className="text-xs text-gray-400 mt-2">
              By completing this payment, you agreed to our{' '}
              <a
                href="https://dineai.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-600 underline"
              >
                Terms of Service
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VirtualHostOrderSuccess;
